import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, MoreHorizontal, ExternalLink, Edit, Trash2, Eye, EyeOff, Search, Copy } from 'lucide-react';
import EmptyState from '../../components/EmptyState';
import useSitesList from '../../hooks/useSitesList';
import SkeletonSitesList from '../../components/skeletons/SkeletonSitesList';

function timeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
}

function SiteThumbnail() {
  return (
    <div className="w-[100px] h-[64px] rounded-md bg-gray-50 border border-gray-200/60 overflow-hidden flex-shrink-0 flex flex-col items-center justify-center p-2 gap-1">
      <div className="w-full h-1.5 bg-gray-200/80 rounded-full" />
      <div className="w-3/4 h-1 bg-gray-200/60 rounded-full" />
      <div className="flex gap-1 w-full mt-0.5">
        <div className="flex-1 h-3 bg-gray-100 rounded-sm" />
        <div className="flex-1 h-3 bg-gray-100 rounded-sm" />
        <div className="flex-1 h-3 bg-gray-100 rounded-sm" />
      </div>
      <div className="w-full h-1 bg-gray-200/40 rounded-full" />
    </div>
  );
}

function ActionsDropdown({ site, onViewPublic, onDuplicate, onPublishToggle, onDelete }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(!open);
        }}
        className="p-1.5 rounded-md text-gray-300 hover:text-gray-500 hover:bg-gray-50 transition-colors"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-44 origin-top-right rounded-lg bg-white shadow-lg border border-gray-100 py-1">
          {site.is_published && site.subdomain && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewPublic(site);
                setOpen(false);
              }}
              className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-2.5 text-gray-400" />
              View Public
            </button>
          )}
          <Link
            to={`/sites/${site.id}`}
            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            onClick={() => setOpen(false)}
          >
            <Edit className="h-3.5 w-3.5 mr-2.5 text-gray-400" />
            Open
          </Link>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(site.id);
              setOpen(false);
            }}
            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Copy className="h-3.5 w-3.5 mr-2.5 text-gray-400" />
            Duplicate
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPublishToggle(site.id, site.is_published);
              setOpen(false);
            }}
            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {site.is_published ? (
              <>
                <EyeOff className="h-3.5 w-3.5 mr-2.5 text-gray-400" />
                Unpublish
              </>
            ) : (
              <>
                <Eye className="h-3.5 w-3.5 mr-2.5 text-gray-400" />
                Publish
              </>
            )}
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(site.id);
              setOpen(false);
            }}
            className="flex items-center w-full px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5 mr-2.5" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function SitesList() {
  const {
    sites,
    loading,
    error,
    showCreateModal,
    newSiteName,
    creating,
    highlightedSiteId,
    setShowCreateModal,
    setNewSiteName,
    fetchSites,
    handleCreateSite,
    handleDeleteSite,
    handleDuplicateSite,
    handlePublishToggle,
  } = useSitesList();

  const [searchQuery, setSearchQuery] = useState('');
  const modalRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setShowCreateModal(false);
        setNewSiteName('');
      }
    }
    if (showCreateModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreateModal, setShowCreateModal, setNewSiteName]);

  const handleViewPublic = (site) => {
    if (site.subdomain) {
      window.open(`https://${site.subdomain}.fasttttt.com`, '_blank', 'noopener,noreferrer');
    }
  };

  const filteredSites = sites.filter(site =>
    site.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <SkeletonSitesList />;

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-sm text-red-500">{error}</p>
          <button
            onClick={fetchSites}
            className="mt-3 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-8 py-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Sites</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create Site
        </button>
      </div>

      {sites.length === 0 ? (
        /* Empty state */
        <EmptyState
          illustration="sites"
          headline="Create your first website"
          description="Build and publish multi-page websites with custom domains. Your site will be live at yourname.fasttttt.com."
          buttonLabel="Create Site"
          onAction={() => setShowCreateModal(true)}
        />
      ) : (
        <>
          {/* Search bar */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search sites..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400 transition-colors"
              />
            </div>
          </div>

          {/* Table */}
          <div>
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_140px_140px_44px] items-center px-4 pb-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-500">Name</span>
              <span className="text-xs font-medium text-gray-500">Pages</span>
              <span className="text-xs font-medium text-gray-500">Status</span>
              <span className="sr-only">Actions</span>
            </div>

            {/* Rows */}
            {filteredSites.length === 0 ? (
              <div className="py-12 text-center text-sm text-gray-400">
                No sites matching "{searchQuery}"
              </div>
            ) : (
              <div>
                {filteredSites.map((site) => (
                  <div
                    key={site.id}
                    data-site-id={site.id}
                    className={`grid grid-cols-[1fr_140px_140px_44px] items-center px-4 py-3 border-b border-gray-100 hover:bg-gray-50/50 transition-colors group ${
                      highlightedSiteId === site.id ? 'bg-brand-50 ring-1 ring-inset ring-brand-200' : ''
                    }`}
                  >
                    {/* Name cell: thumbnail + name + subdomain — this is the clickable link */}
                    <Link
                      to={`/sites/${site.id}`}
                      className="flex items-center gap-4 min-w-0"
                    >
                      <SiteThumbnail />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate group-hover:text-brand-600 transition-colors">
                          {site.name}
                        </p>
                        {site.subdomain ? (
                          <p className="text-xs text-gray-400 mt-0.5">{site.subdomain}.fasttttt.com</p>
                        ) : (
                          <p className="text-xs text-gray-400 mt-0.5">
                            Created {timeAgo(site.created_at)}
                          </p>
                        )}
                      </div>
                    </Link>

                    {/* Pages count */}
                    <div className="text-sm text-gray-500">
                      {site.pages ? site.pages.length : 0}
                    </div>

                    {/* Status */}
                    <div>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          site.is_published
                            ? 'bg-brand-50 text-brand-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {site.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>

                    {/* Actions */}
                    <div>
                      <ActionsDropdown
                        site={site}
                        onViewPublic={handleViewPublic}
                        onDuplicate={handleDuplicateSite}
                        onPublishToggle={handlePublishToggle}
                        onDelete={handleDeleteSite}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Create Site Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div
            ref={modalRef}
            className="w-full max-w-sm mx-4 bg-white rounded-xl shadow-lg border border-gray-100 p-6"
          >
            <h3 className="text-base font-semibold text-gray-900">Create New Site</h3>
            <p className="mt-1 text-sm text-gray-500">Give your site a name to get started.</p>
            <form onSubmit={handleCreateSite} className="mt-5">
              <input
                type="text"
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.target.value)}
                placeholder="Site name"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400 transition-colors"
                disabled={creating}
                autoFocus
              />
              <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewSiteName('');
                  }}
                  className="px-3.5 py-1.5 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newSiteName.trim() || creating}
                  className="px-3.5 py-1.5 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {creating ? 'Creating...' : 'Create Site'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
